import { useMemo } from "react";
import { Box, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { CalendarSlot, MissedSession } from "../types";
import { ClientRow } from "./listTypes";
import { getShortName, getShortWeekDayLabel, formatTime } from "./listHelpers";
import { processClientRows } from "./listUtils";
import { Avatar } from "../../../components/fieldsViews/fields/Avatar";
import { useField } from "../../../api/useField";
import { CustomChip } from "../../../components/chips/CustomChip";

type MissedSessionsListProps = {
    allSlots: Array<CalendarSlot>;
    showNfb: boolean;
};

type GroupedClients = {
    ownerName: string | null;
    ownerAvatar: string | null;
    clients: Array<ClientRow>;
};

const ReservationChips = ({ reservations, color }: { reservations: Array<{ weekDay: number; time: string }>; color: "warning" | "primary" }) => {
    if (reservations.length === 0) {
        return <Typography variant="caption" color="text.disabled">-</Typography>;
    }

    return (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {reservations.map((reservation, index) => (
                <Chip
                    key={index}
                    label={`${getShortWeekDayLabel(reservation.weekDay)} ${formatTime(reservation.time)}`}
                    size="small"
                    color={color}
                    variant="outlined"
                    sx={{ fontSize: "0.8rem", height: 22 }}
                />
            ))}
        </Box>
    );
};

const MissedSessionsText = ({ sessions }: { sessions: Array<MissedSession> }) => {
    if (sessions.length === 0) {
        return <Typography variant="caption" color="text.disabled">-</Typography>;
    }

    const formattedDates = sessions.map((session) => {
        const dateParts = session.date.split("-").reverse().slice(0, 2).join("/");
        return dateParts;
    });

    const isCritical = sessions.length > 2;

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Chip
                label={sessions.length}
                size="small"
                color="error"
                variant={isCritical ? "filled" : "outlined"}
                sx={{ fontSize: "0.8rem", height: 22, minWidth: 28 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
                {formattedDates.join(", ")}
            </Typography>
        </Box>
    );
};

const groupClientsByOwner = (clients: Array<ClientRow>): Array<GroupedClients> => {
    const groupsMap = new Map<string, GroupedClients>();

    clients.forEach((client) => {
        const key = client.clientOwnerName ?? "__no_owner__";

        if (!groupsMap.has(key)) {
            groupsMap.set(key, {
                ownerName: client.clientOwnerName,
                ownerAvatar: client.clientOwnerAvatar,
                clients: [],
            });
        }

        groupsMap.get(key)!.clients.push(client);
    });

    const groups = Array.from(groupsMap.values());
    groups.sort((a, b) => {
        const nameA = a.ownerName ?? "";
        const nameB = b.ownerName ?? "";
        return nameA.localeCompare(nameB);
    });

    return groups;
};

export const MissedSessionsList = ({ allSlots, showNfb }: MissedSessionsListProps) => {
    const { formatedField: treatmentStatusField } = useField("treatments", "treatment_status_new");

    const groupedClients = useMemo(() => {
        const allClientRows = processClientRows(allSlots, false);
        const filteredClients = allClientRows.filter((client) => client.missedNfb || client.missedPsy);
        return groupClientsByOwner(filteredClients);
    }, [allSlots]);

    const totalClients = groupedClients.reduce((acc, group) => acc + group.clients.length, 0);

    if (totalClients === 0) {
        return (
            <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                    Não existem clientes com sessões falhadas consecutivas.
                </Typography>
            </Box>
        );
    }

    const columnCount = showNfb ? 7 : 5;

    return (
        <TableContainer sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ backgroundColor: "background.paper", fontWeight: "bold", minWidth: 220 }}>
                            Cliente
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "background.paper", fontWeight: "bold" }}>
                            Estado
                        </TableCell>
                        {showNfb && (
                            <>
                                <TableCell sx={{ backgroundColor: "background.paper", fontWeight: "bold" }}>
                                    Reservas NFB
                                </TableCell>
                                <TableCell sx={{ backgroundColor: "background.paper", fontWeight: "bold" }}>
                                    Sessões Falhadas NFB
                                </TableCell>
                            </>
                        )}
                        <TableCell sx={{ backgroundColor: "background.paper", fontWeight: "bold" }}>
                            Psicóloga
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "background.paper", fontWeight: "bold" }}>
                            Reservas PSY
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "background.paper", fontWeight: "bold" }}>
                            Sessões Falhadas PSY
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {groupedClients.map((group) => (
                        <>
                            <TableRow key={`group_${group.ownerName ?? "no_owner"}`}>
                                <TableCell
                                    colSpan={columnCount}
                                    sx={{
                                        backgroundColor: "#e3f2fd",
                                        py: 1.5,
                                        borderBottom: "1px solid #90caf9",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <Avatar value={group.ownerAvatar ?? undefined} styles={{ width: 28, height: 28 }} />
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: "0.95rem" }}>
                                            {group.ownerName ? getShortName(group.ownerName) : "Sem Gestor de Caso"}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ({group.clients.length} {group.clients.length === 1 ? "cliente" : "clientes"})
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                            {group.clients.map((client) => (
                                <TableRow key={`${client.clientId}_${client.treatmentId}`} hover>
                                    <TableCell sx={{ backgroundColor: "#fafafa", py: 1, whiteSpace: "nowrap", pl: 5 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.95rem" }}>
                                                {client.clientId} - {getShortName(client.clientName)}
                                            </Typography>
                                            <IconButton
                                                component={Link}
                                                to={`/treatments/${client.treatmentId}/sessions`}
                                                target="_blank"
                                                size="small"
                                                color="secondary"
                                                sx={{ p: 0.25 }}
                                            >
                                                <OpenInNew sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: "#fafafa", py: 1 }}>
                                        {treatmentStatusField && client.treatmentStatus ? (
                                            <CustomChip
                                                field={treatmentStatusField}
                                                label={client.treatmentStatus}
                                                extraOptions={{ style: { size: "small" } }}
                                            />
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">-</Typography>
                                        )}
                                    </TableCell>
                                    {showNfb && (
                                        <>
                                            <TableCell sx={{ backgroundColor: "#fafafa", py: 1 }}>
                                                <ReservationChips reservations={client.nfbReservations} color="warning" />
                                            </TableCell>
                                            <TableCell sx={{ backgroundColor: "#fafafa", py: 1 }}>
                                                <MissedSessionsText sessions={client.missedNfbSessions} />
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell sx={{ backgroundColor: "#fafafa", py: 1, whiteSpace: "nowrap" }}>
                                        {client.psyTherapistName ? (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Avatar value={client.psyTherapistAvatar ?? undefined} styles={{ width: 24, height: 24 }} />
                                                <Typography variant="body2" sx={{ fontSize: "0.95rem" }}>
                                                    {getShortName(client.psyTherapistName)}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: "#fafafa", py: 1 }}>
                                        <ReservationChips reservations={client.psyReservations} color="primary" />
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: "#fafafa", py: 1 }}>
                                        <MissedSessionsText sessions={client.missedPsySessions} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
